<template>
  <transition name="photo-editor-sidebar">
    <div v-if="visible" class="photo-editor-sidebar" @click.self="$emit('close')">
      <aside class="photo-editor-sidebar__panel" @click.stop>
        <header class="photo-editor-sidebar__header">
          <button type="button" class="photo-editor-sidebar__close" aria-label="Закрыть" @click="$emit('close')">
            <v-icon-cross />
          </button>
          <h2 class="photo-editor-sidebar__title">Редактирование</h2>
          <button type="button" class="photo-editor-sidebar__reset" @click="resetForm">
            Сброс
          </button>
        </header>

        <form class="photo-editor-sidebar__form" @submit.prevent="submitForm">
          <div class="photo-editor-sidebar__field">
            <label class="photo-editor-sidebar__label" for="photo-editor-tags">ТЕГИ</label>
            <v-input
              id="photo-editor-tags"
              :value="form.tag"
              :disabled="tagDisabled"
              placeholder="Добавить тег"
              class="photo-editor-sidebar__input"
              @input="form.tag = $event"
            />
            <span v-if="tagDisabled" class="photo-editor-sidebar__hint">
              Тег можно добавить только после публикации фотографии
            </span>
          </div>

          <div class="photo-editor-sidebar__actions">
            <v-button-wide class="photo-editor-sidebar__btn" @click="$emit('close')">
              Отменить
            </v-button-wide>
            <v-button-wide class="photo-editor-sidebar__btn photo-editor-sidebar__btn--primary" @click="submitForm">
              Обновить
            </v-button-wide>
          </div>
        </form>

        <div class="photo-editor-sidebar__footer">
          <button type="button" class="photo-editor-sidebar__delete" @click="onDelete">
            Удалить фотографию
          </button>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import VIconCross from '@/components/icons/VCross.vue'
import VButtonWide from '@/components/common/VButtonWide.vue'
import VInput from '@/components/common/VInput.vue'

export default {
  name: 'VSidebarPhotoEditor',
  components: { VIconCross, VButtonWide, VInput },
  props: {
    visible: { type: Boolean, default: false },
    photo: { type: Object, default: null },
    tagDisabled: { type: Boolean, default: false }
  },
  data() {
    return {
      form: {
        tag: ''
      }
    }
  },
  watch: {
    photo: {
      immediate: true,
      handler(photo) {
        this.syncFormFromPhoto(photo)
      }
    }
  },
  methods: {
    syncFormFromPhoto(photo) {
      if (!photo) {
        this.form = { tag: '' }
        return
      }
      this.form = {
        tag: photo.tag || ''
      }
    },
    resetForm() {
      this.syncFormFromPhoto(this.photo)
    },
    submitForm() {
      if (!this.photo) return
      this.$emit('save', {
        ...this.photo,
        tag: this.form.tag
      })
      this.$emit('close')
    },
    onDelete() {
      if (this.photo) {
        this.$emit('delete', this.photo)
      }
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/media';
@import 'src/assets/scss/mixins';

.photo-editor-sidebar {
  position: fixed;
  inset: 0;
  z-index: $--z-index-high;
  background-color: $--pitch-black-40percent;
  display: flex;
  justify-content: flex-end;

  &__panel {
    width: 100%;
    max-width: $--v-drawer-width;
    height: 100%;
    background-color: $--white;
    display: flex;
    flex-direction: column;
    box-shadow: -2px 0 8px $--pitch-black-20percent;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid $--medium-gray;
    flex-shrink: 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: $--main-border-radius;
    color: $--pitch-black;

    &:hover {
      background-color: $--light-gray-300;
    }

    .icon {
      width: 24px;
      height: 24px;
    }
  }

  &__title {
    flex: 1;
    margin: 0;
    font-size: $--font-size-base;
    font-weight: $--font-weight-semi-bold;
    line-height: 1.4;
    text-align: center;
  }

  &__reset {
    padding: 6px 0 6px 12px;
    font-size: $--font-size-base;
    color: $--gray-600;
    background: transparent;
    cursor: pointer;

    &:hover {
      color: $--pitch-black;
      border-color: $--gray-600;
    }
  }

  &__form {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__label {
    font-size: $--font-size-small;
    font-weight: $--font-weight-medium;
    color: $--pitch-black;
  }

  &__input {
    width: 100%;
  }

  &__hint {
    font-size: $--font-size-small;
    color: $--gray-600;
  }

  &__actions {
    flex-direction: column;
    display: flex;
    gap: 12px;
    margin-top: 8px;
  }

  &__btn {
    flex: 1;
    text-align: center;
    border: 1px solid black;
    border-radius: 4px;

    &--primary {
      @include themify() {
        background-color: themed('primary');
        color: $--white;
        border-color: themed('primary');
      }
    }
  }

  &__footer {
    padding: 20px;
    border-top: 1px solid $--medium-gray;
    flex-shrink: 0;
  }

  &__delete {
    width: 100%;
    padding: 10px 16px;
    font-size: $--font-size-base;
    background: transparent;
    border-radius: $--main-border-radius;
    font-weight: $--font-weight-semi-bold;
    cursor: pointer;
  }
}

.photo-editor-sidebar-enter-active,
.photo-editor-sidebar-leave-active {
  transition: opacity $--transition-duration ease;
}
.photo-editor-sidebar-enter-active .photo-editor-sidebar__panel,
.photo-editor-sidebar-leave-active .photo-editor-sidebar__panel {
  transition: transform $--transition-duration ease;
}
.photo-editor-sidebar-enter,
.photo-editor-sidebar-leave-to {
  opacity: 0;
}
.photo-editor-sidebar-enter .photo-editor-sidebar__panel,
.photo-editor-sidebar-leave-to .photo-editor-sidebar__panel {
  transform: translateX(100%);
}
</style>
